import { customElement } from "solid-element";
import { createSignal, onMount, For } from "solid-js";
import { setTranslations, translations } from "../../services/context";
import { getTranslations } from "../../services/translation";

const officeApiUrl = process.env.OFFICE_API;

customElement("builderall-confirm-email", { email: "", lang: "" }, (props) => {
  const [code, setCode] = createSignal(Array(6).fill(""));
  const [canResend, setCanResend] = createSignal(true);
  const [timer, setTimer] = createSignal(0);
  const [loading, setLoading] = createSignal(false);
  let inputs = [];

  onMount(async () => {
    // Only works if the component is not inside a builderall-appshell tag
    if (props.lang) {
      setTranslations(await getTranslations(props.lang));
    }

    await checkEmailStatus();
  });

  const emitEvent = (detail) => {
    const customEvent = new CustomEvent("builderall-confirm-email", {
      bubbles: true,
      composed: true,
      detail
    })

    document.dispatchEvent(customEvent)
  };

  const startTimer = (seconds = 60) => {
    setCanResend(false);
    setTimer(seconds);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const checkEmailStatus = async () => {
    try {
      const res = await fetch(`${officeApiUrl}/v1/email-verification/${props.email}`, {
        headers: {
          Accept: "application/json",
        },
      });
      const data = await res.json();
      if (!data.data.verified) {
        await sendVerificationCode();
      } else {
        emitEvent({
          status: true,
          slug: "email_already_verified",
          message: translations['ce-email-already-verified']
        });
      }
    } catch (err) {
      emitEvent({
        status: false,
        slug: "error_checking_email",
        message: translations['ce-error-checking-email']
      });
      console.error(translations['ce-error-checking-email'], err);
    }
  };

  const sendVerificationCode = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${officeApiUrl}/v1/email-verification/${props.email}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ locale: props.lang })
      });
      const data = await res.json();

      if (data.data?.wait_seconds) {
        startTimer(data.data.wait_seconds || 60);
      } else {
        setCanResend(true);
      }

      return true;
    } catch (err) {
      emitEvent({
        status: false,
        slug: "error_sending_code",
        message: translations['ce-error-sending-code']
      });
      console.error(translations['ce-error-sending-code'], err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleResendClick = async () => {
    const success = await sendVerificationCode();

    if (success) {
      emitEvent({
        status: true,
        slug: "code_resent",
        message: translations['ce-code-sent-successfully']
      });
    }
  };

  const verifyCode = async () => {
    const finalCode = code().join("");
    if (finalCode.length !== 6) return alert(translations['ce-please-enter-correctly-characters']);

    try {
      setLoading(true);
      const res = await fetch(`${officeApiUrl}/v1/email-verification/${props.email}`, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ code: finalCode }),
      });

      const result = await res.json();

      if (res.ok && result.success) {
        emitEvent({
          status: true,
          slug: "correct_code",
          message: translations['ce-email-verified-successfully']
        });
      } else {
        emitEvent({
          status: false,
          slug: "invalid_code",
          message: translations['ce-invalid-code']
        });
      }
    } catch (err) {
      emitEvent({
        status: false,
        slug: "error_verifying_code",
        message: translations['ce-error-verifying-code']
      });
      console.error(translations['ce-error-verifying-code'], err);
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e, index) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1);

    setCode((prev) => {
      const newCode = [...prev];
      newCode[index] = value;
      return newCode;
    });

    if (value && index < inputs.length - 1) {
      inputs[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" || e.keyCode === 8) {
      if (code()[index]) {
        setCode((prev) => {
          const newCode = [...prev];
          newCode[index] = "";
          return newCode;
        });
        inputs[index]?.focus();
        e.preventDefault();
        return;
      }

      if (!code()[index] && index > 0) {
        inputs[index - 1]?.focus();
        e.preventDefault();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;

    const newCode = Array(6)
      .fill("")
      .map((_, i) => pastedData[i] || "");

    setCode(newCode);

    const lastIndex = Math.min(pastedData.length, inputs.length) - 1;
    if (lastIndex >= 0) inputs[lastIndex]?.focus();
  };


  return (
    <>
      <style>{require("bundle-text:./styles/index.scss")}</style>

      <div class="confirm-email-section">
        <div class="numbers-box">
          <For each={code()}>
            {(char, i) => (
              <>
                <input
                  ref={(el) => (inputs[i()] = el)}
                  type="text"
                  value={char}
                  maxlength="1"
                  onInput={(e) => handleInput(e, i())}
                  onKeyDown={(e) => handleKeyDown(e, i())}
                  onPaste={i() === 0 ? handlePaste : undefined}
                  inputmode="numeric"
                />
                {i() === 2 && <div class="horizontal-line" />}
              </>
            )}
          </For>
        </div>

        <div class="send-code-again-box">
          <button onClick={handleResendClick} disabled={loading() || !canResend()}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_373_3928)"><path d="M19.9921 10.0079C19.9921 9.56994 19.9556 9.15427 19.9064 8.75998C19.891 8.61315 19.8464 8.47088 19.7753 8.34148C19.7042 8.21209 19.608 8.09817 19.4924 8.00639C19.3767 7.91462 19.2439 7.84682 19.1018 7.80698C18.9596 7.76715 18.8109 7.75606 18.6644 7.77437C18.5179 7.79268 18.3766 7.84003 18.2486 7.91363C18.1206 7.98724 18.0086 8.08563 17.9191 8.20305C17.8296 8.32047 17.7644 8.45456 17.7274 8.59747C17.6903 8.74039 17.6821 8.88926 17.7033 9.03537C17.7451 9.3705 17.7716 9.69203 17.7716 10.0079C17.7716 14.3135 14.3056 17.7795 9.99997 17.7795C5.69435 17.7795 2.22834 14.3135 2.22834 10.0079C2.22834 5.70229 5.69435 2.23628 9.99997 2.23628C11.4627 2.23628 12.8207 2.64642 13.9844 3.34651L13.8858 3.34651C13.7387 3.34443 13.5926 3.37161 13.4561 3.42647C13.3196 3.48133 13.1953 3.56278 13.0905 3.66608C12.9858 3.76938 12.9026 3.89247 12.8458 4.02821C12.789 4.16394 12.7597 4.30961 12.7597 4.45674C12.7597 4.60388 12.789 4.74954 12.8458 4.88528C12.9026 5.02101 12.9858 5.14411 13.0905 5.24741C13.1953 5.35071 13.3196 5.43215 13.4561 5.48702C13.5926 5.54188 13.7387 5.56906 13.8858 5.56698L16.9324 5.56698L17.2165 5.56698C17.5109 5.56695 17.7933 5.44997 18.0015 5.24176C18.2097 5.03356 18.3267 4.75119 18.3267 4.45674L18.3267 1.12605C18.3288 0.978926 18.3016 0.832861 18.2468 0.696337C18.1919 0.559814 18.1104 0.435555 18.0071 0.330783C17.9038 0.226011 17.7808 0.142814 17.645 0.0860282C17.5093 0.0292424 17.3636 -1.14893e-07 17.2165 -1.21325e-07C17.0694 -1.27756e-07 16.9237 0.0292423 16.7879 0.0860282C16.6522 0.142814 16.5291 0.226011 16.4258 0.330783C16.3225 0.435555 16.2411 0.559814 16.1862 0.696337C16.1314 0.83286 16.1042 0.978926 16.1063 1.12604L16.1063 2.10834C14.4165 0.799526 12.2973 0.0158121 9.99997 0.015812C4.49446 0.0158117 0.00787715 4.5024 0.00787691 10.0079C0.00787667 15.5134 4.49446 20 9.99997 20C15.5055 20 19.9921 15.5134 19.9921 10.0079Z" fill="#0072E1" /></g><defs><clipPath id="clip0_373_3928"><rect width="20" height="20" fill="white" transform="translate(20) rotate(90)" /></clipPath></defs></svg>
            <span>{loading() ? `${translations['ce-sending']}...` : translations['ce-send-code-again']}</span>
          </button>
        </div>

        {timer() > 0 && (
          <div class="resend-time-box">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_412_178)"><path d="M2.17121 13.4193H0.027832C0.511093 19.8844 5.91335 25 12.5 25C19.0866 25 24.4889 19.8844 24.9721 13.4193H22.8287C22.3098 13.4193 21.8898 12.9986 21.8898 12.4803C21.8898 11.962 22.3098 11.5413 22.8287 11.5413H24.9721C24.8582 10.0189 24.472 8.57476 23.8604 7.25205L21.5586 8.58978C21.4103 8.67617 21.2475 8.71685 21.0879 8.71685C20.7642 8.71685 20.4494 8.54909 20.2753 8.24987C20.0143 7.80104 20.1671 7.22638 20.6153 6.96597L22.9527 5.60758C22.0963 4.30991 21.004 3.18564 19.7395 2.28485L18.4875 4.3481C18.311 4.63918 18.0011 4.80006 17.6838 4.80006C17.5179 4.80006 17.3495 4.75624 17.1974 4.6636C16.7542 4.39442 16.6127 3.81664 16.8819 3.37344L18.132 1.31394C16.701 0.589053 15.1166 0.127701 13.439 0V2.15089C13.439 2.6692 13.0189 3.08987 12.5 3.08987C11.981 3.08987 11.561 2.6692 11.561 2.15089V0.000625941C9.88336 0.128327 8.29898 0.589679 6.86798 1.31457L8.11807 3.37406C8.38725 3.81726 8.24578 4.39505 7.80258 4.66422C7.65046 4.75624 7.48207 4.80069 7.31619 4.80069C6.99881 4.80069 6.68957 4.63981 6.51242 4.34872L5.26045 2.28547C3.99596 3.18627 2.90299 4.31054 2.04726 5.60821L4.38469 6.9666C4.8329 7.22701 4.98501 7.80166 4.7246 8.25049C4.55058 8.55034 4.23571 8.71748 3.91208 8.71748C3.75182 8.71748 3.58969 8.67679 3.44133 8.59041L1.13958 7.25267C0.527995 8.57601 0.141761 10.0202 0.027832 11.5419H2.17121C2.69015 11.5419 3.11019 11.9626 3.11019 12.4809C3.11019 12.9992 2.69015 13.4193 2.17121 13.4193ZM10.622 12.4803C10.622 11.443 11.4627 10.6023 12.5 10.6023C12.6671 10.6023 12.8261 10.6311 12.9801 10.6724L15.9047 7.74783C16.2715 7.381 16.8656 7.381 17.2324 7.74783C17.5993 8.11466 17.5993 8.70872 17.2324 9.07554L14.3078 12.0002C14.3485 12.1541 14.3779 12.3131 14.3779 12.4803C14.3779 13.5175 13.5372 14.3582 12.5 14.3582C11.4627 14.3582 10.622 13.5175 10.622 12.4803Z" fill="#0072E1" /></g><defs><clipPath id="clip0_412_178"><rect width="25" height="25" fill="white" /></clipPath></defs></svg>
            <span>{timer()} {translations['ce-seconds-to-resend']}</span>
          </div>
        )}

        <div class="send-code-box">
          <button onClick={verifyCode} disabled={loading() || code().join("").length !== 6}>
            {loading() ? `${translations['ce-verifying']}...` : translations['ce-next']}
          </button>
        </div>
      </div>
    </>
  );
});
